'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiExternalLink, FiGithub } from 'react-icons/fi';
import { Button, SectionHeader, SectionCard, Tag } from '@/shared/ui';
import { projectsMock } from '@/shared/store/mocks';
import styles from './projects-section.module.scss';

gsap.registerPlugin(ScrollTrigger);

export default function ProjectsSection() {
  const { title, subtitle, projects, viewLabel, githubAriaLabel } = projectsMock;
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(titleRef.current, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.8, scrollTrigger: { trigger: titleRef.current, start: 'top 80%' } });
      const cards = gridRef.current?.querySelectorAll(`.${styles.card}`);
      if (cards?.length) gsap.fromTo(cards, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, scrollTrigger: { trigger: gridRef.current, start: 'top 85%' } });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="projects" ref={sectionRef} className={styles.section}>
      <div className={styles.container}>
        <SectionHeader
          ref={titleRef}
          title={title}
          subtitle={subtitle}
          classNames={{ root: styles.titleBlock, title: styles.title, line: styles.titleLine, subtitle: styles.subtitle }}
        />
        <div ref={gridRef} className={styles.grid}>
          {projects.map((p, i) => (
            <div key={p.title} className={styles.cardWrap}>
              <SectionCard
                classNames={{ root: styles.card, hover: styles.cardHover, glow: styles.cardGlow }}
              >
                <span className={styles.number}>{String(i + 1).padStart(2, '0')}</span>
                <h3 className={styles.cardTitle}>{p.title}</h3>
                <p className={styles.cardDesc}>{p.description}</p>
                <div className={styles.techList}>
                  {p.technologies.map((t) => (
                    <Tag key={t} className={styles.techTag}>{t}</Tag>
                  ))}
                </div>
                <div className={styles.actions}>
                  <Button type="button" className={styles.btn} startIcon={<FiExternalLink size={16} />}>
                    {viewLabel}
                  </Button>
                  <Button type="button" className={styles.btnIcon} aria-label={githubAriaLabel} startIcon={<FiGithub size={16} />} />
                </div>
              </SectionCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
